/**
 * TEST-INSTRUMENTIERUNG (Race-Condition-Isolation).
 *
 * Künstliche Verzögerung an den Daten-Ladepunkten, damit die non-suspending
 * Loading-States (Food-/Purchase-Caches, Rezept-Loader) deterministisch
 * sichtbar werden. Standardmäßig inaktiv (0) – hat in Produktion keinen Effekt.
 *
 * Steuerung aus Playwright heraus (in dieser Reihenfolge):
 *   - `window.__P2P_LOAD_DELAY_MS__ = 3000`
 *   - `localStorage.setItem("p2pLoadDelay", "3000")` (überlebt Reloads)
 *   - URL-Parameter `/plan?p2pLoadDelay=3000`
 */
const STORAGE_KEY = "p2pLoadDelay";

export function getLoadDelayMs(): number {
  if (typeof window === "undefined") return 0;

  const globalWindow = window as unknown as {
    __P2P_LOAD_DELAY_MS__?: unknown;
  };

  const readStorage = () => {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  };

  const readSearchParam = () => {
    try {
      return new URLSearchParams(window.location.search).get(STORAGE_KEY);
    } catch {
      return null;
    }
  };

  for (const raw of [
    globalWindow.__P2P_LOAD_DELAY_MS__,
    readStorage(),
    readSearchParam(),
  ]) {
    const value = Number(raw);
    if (Number.isFinite(value) && value > 0) return value;
  }
  return 0;
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Liefert das Ergebnis von `promise` erst nach der konfigurierten Verzögerung.
 * Ohne gesetzten Delay wird das Original-Promise unverändert durchgereicht.
 */
export async function withLoadDelay<T>(promise: Promise<T>): Promise<T> {
  const ms = getLoadDelayMs();
  if (ms <= 0) return promise;
  const [value] = await Promise.all([promise, sleep(ms)]);
  return value;
}
